import axios, { AxiosResponse } from 'axios';

export type ErrorResult = {
    errors: any;
    status: number;
    title: string;
    traceId: string;
    type: string;
}

const silentAxiosInstance = axios.create();

export function isAccessDenied(response: AxiosResponse): boolean {
    if (response?.status === 401) {
        // 401 Unauthorized - token expired or invalid
        // Redirect to home which will trigger the auth check in the loader
        console.log('401 Unauthorized - redirecting to trigger auth flow');
        document.location.href = '/';
        return true;
    }
    if (response?.status === 403) {
        // 403 Forbidden - user doesn't have permission
        console.log('403 Forbidden - redirecting to access denied');
        document.location.href = '/access-denied';
        return true;
    }
    return false;
}

export function getFromApi(url: string, onSuccess: { (data: any): void }, onFail: { (error?: any): void }): void {
    axios.get(url)
        .then(response => onSuccess && onSuccess(response.data))
        .catch(error => {
            if (isAccessDenied(error.response)) {
                return;
            }
            onFail?.(error);
        });
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function postToApi(url: string, data: any, onSuccess: { (data: any): void }, onFail: { (result: ErrorResult): void }, always?: { (): void }): void {
    axios.post(url, data)
        .then(response => {
            onSuccess?.(response.data);
        })
        .catch(error => {
            console.log('post failed', error);
            if (isAccessDenied(error.response)) {
                return;
            }
            const result = error.response ? error.response.data : null;
            onFail?.(result);
        })
        .finally(() => {
            always?.();
        });
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function postStringToApi(url: string, data: string, onSuccess: { (data: any): void }, onFail: { (result: ErrorResult): void }, always?: { (): void }): void {
    axios.post(url, JSON.stringify(data), {
            headers: {
                'Content-Type': 'application/json'
            }
        })
        .then(response => {
            onSuccess?.(response.data);
        })
        .catch(error => {
            if (isAccessDenied(error.response)) {
                return;
            }
            const result = error.response ? error.response.data : null;
            onFail?.(result);
        })
        .finally(() => {
            always?.();
        });
}

export function putStringToApi(url: string, data: string, onSuccess: { (data: any): void }, onFail: { (result: ErrorResult): void }, always?: { (): void }): void {
    axios.put(url, JSON.stringify(data), {
            headers: {
                'Content-Type': 'application/json'
            },
        })
        .then(response => {
            onSuccess?.(response.data);
        })
        .catch(error => {
            if (isAccessDenied(error.response)) {
                return;
            }
            const result = error.response ? error.response.data : null;
            onFail?.(result);
        })
        .finally(() => {
            always?.();
        });
}

//eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function putToApi(url: string, data: any, onSuccess: { (data: any): void }, onFail: { (result: ErrorResult): void }, always?: { (): void }): void {
    axios.put(url, data)
        .then(response => {
            onSuccess?.(response.data);
        })
        .catch(error => {
            console.log('put failed', error);
            if (isAccessDenied(error.response)) {
                return;
            }
            const result = error.response ? error.response.data : null;
            onFail?.(result);
        })
        .finally(() => {
            always?.();
        });
}

export function deleteToApi(url: string, onSuccess: { (): void }, onFail: { (result: ErrorResult): void }, always?: { (): void }): void {
    axios.delete(url)
        .then(() => {
            onSuccess?.();
            always?.();
        })
        .catch(error => {
            console.log('delete failed', error);
            if (isAccessDenied(error.response)) {
                return;
            }
            const result = error.response ? error.response.data : null;
            onFail?.(result);
        })
        .finally(() => {
            always?.();
        });
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function deleteWithBodyToApi(url: string, data: any, onSuccess: { (): void }, onFail: { (result: ErrorResult): void }, always?: { (): void }): void {
    axios.delete(url, { data: data })
        .then(() => {
            onSuccess?.();
        })
        .catch(error => {
            console.log('delete failed', error);
            if (isAccessDenied(error.response)) {
                return;
            }
            const result = error.response ? error.response.data : null;
            onFail?.(result);
        })
        .finally(() => {
            always?.();
        });
}

// Get data from API without triggering loading indicator
export function getSilentFromApi(url: string, onSuccess: { (data: any): void }, onFail: { (): void }): void {
    ensureUpdatedToken();
    silentAxiosInstance.get(url)
        .then(response => onSuccess?.(response.data))
        .catch(error => {
            if (isAccessDenied(error.response)) {
                return;
            }
            onFail?.();
        });
}

export function buildQueryString(parameters: object): string {
    if (!parameters) {
        return '';
    }

    const keysWithValues = Object.keys(parameters).filter(key => parameters[key] !== undefined && parameters[key] !== null);
    const query = keysWithValues.map(key => `${key}=${parameters[key]}`).join('&');
    return query ? `?${query}` : '';
}

function ensureUpdatedToken() {
    silentAxiosInstance.defaults.headers.common['Authorization'] = axios.defaults.headers.common['Authorization'];
}

export const axiosFetcher = (url: string): Promise<any> => axios.get(url).then(res => res.data);
